import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ApiAccessSection = () => {
  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      name: 'Production API Key',
      key: 'cp_live_1234567890abcdef',
      created: '2025-10-15T10:30:00Z',
      lastUsed: '2025-11-06T14:20:00Z',
      permissions: ['read:valuations', 'write:valuations', 'read:inventory'],
      status: 'active'
    },
    {
      id: 2,
      name: 'Development Key',
      key: 'cp_test_abcdef1234567890',
      created: '2025-09-20T16:45:00Z',
      lastUsed: '2025-11-05T09:15:00Z',
      permissions: ['read:valuations'],
      status: 'active'
    }
  ]);

  const [webhooks, setWebhooks] = useState([
    {
      id: 1,
      url: 'https://api.example.com/webhooks/cranes',
      events: ['valuation.completed', 'inventory.updated'],
      status: 'active',
      created: '2025-10-20T12:00:00Z',
      lastDelivery: '2025-11-06T15:30:00Z',
      deliveryStatus: 'success'
    }
  ]);

  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    permissions: []
  });

  const [newWebhookForm, setNewWebhookForm] = useState({
    url: '',
    events: []
  });

  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [showNewWebhookForm, setShowNewWebhookForm] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});

  const availablePermissions = [
    { key: 'read:valuations', label: 'Read Valuations', description: 'Access valuation data and reports' },
    { key: 'write:valuations', label: 'Create Valuations', description: 'Generate new valuations' },
    { key: 'read:inventory', label: 'Read Inventory', description: 'Access inventory data' },
    { key: 'write:inventory', label: 'Manage Inventory', description: 'Add and update inventory items' },
    { key: 'read:comparables', label: 'Read Comparables', description: 'Access comparable sales data' }
  ];

  const availableEvents = [
    { key: 'valuation.completed', label: 'Valuation Completed', description: 'Triggered when a valuation is finished' },
    { key: 'valuation.updated', label: 'Valuation Updated', description: 'Triggered when a valuation is modified' },
    { key: 'inventory.added', label: 'Inventory Added', description: 'Triggered when new inventory is added' },
    { key: 'inventory.updated', label: 'Inventory Updated', description: 'Triggered when inventory is modified' },
    { key: 'alert.triggered', label: 'Alert Triggered', description: 'Triggered when price alerts fire' }
  ];

  const handleCreateApiKey = () => {
    if (!newKeyForm?.name?.trim() || newKeyForm?.permissions?.length === 0) {
      return;
    }

    const newKey = {
      id: Date.now(),
      name: newKeyForm?.name,
      key: `cp_live_${Math.random()?.toString(36)?.substring(2, 18)}`,
      created: new Date()?.toISOString(),
      lastUsed: null,
      permissions: newKeyForm?.permissions,
      status: 'active'
    };

    setApiKeys(prev => [...prev, newKey]);
    setNewKeyForm({ name: '', permissions: [] });
    setShowNewKeyForm(false);
    console.log('Created new API key:', newKey);
  };

  const handleRevokeApiKey = (keyId) => {
    setApiKeys(prev => prev?.map(key => 
      key?.id === keyId ? { ...key, status: 'revoked' } : key
    ));
    console.log('Revoked API key:', keyId);
  };

  const handleCreateWebhook = () => {
    if (!newWebhookForm?.url?.trim() || newWebhookForm?.events?.length === 0) {
      return;
    }

    const newWebhook = {
      id: Date.now(),
      url: newWebhookForm?.url,
      events: newWebhookForm?.events,
      status: 'active',
      created: new Date()?.toISOString(),
      lastDelivery: null,
      deliveryStatus: 'pending'
    };

    setWebhooks(prev => [...prev, newWebhook]);
    setNewWebhookForm({ url: '', events: [] });
    setShowNewWebhookForm(false);
    console.log('Created new webhook:', newWebhook);
  };

  const handleDeleteWebhook = (webhookId) => {
    setWebhooks(prev => prev?.filter(webhook => webhook?.id !== webhookId));
    console.log('Deleted webhook:', webhookId);
  };

  const handleTestWebhook = (webhookId) => {
    console.log('Testing webhook:', webhookId);
    // Here you would typically send a test payload to the webhook
  };

  const toggleApiKeyVisibility = (keyId) => {
    setShowApiKey(prev => ({
      ...prev,
      [keyId]: !prev?.[keyId]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    console.log('Copied to clipboard:', text);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp)?.toLocaleString();
  };

  const handlePermissionChange = (permission, checked) => {
    setNewKeyForm(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev?.permissions, permission]
        : prev?.permissions?.filter(p => p !== permission)
    }));
  };

  const handleEventChange = (event, checked) => {
    setNewWebhookForm(prev => ({
      ...prev,
      events: checked 
        ? [...prev?.events, event]
        : prev?.events?.filter(e => e !== event)
    }));
  };

  return (
    <div className="space-y-6">
      {/* API Keys */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Key" size={20} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">API Keys</h3>
              <p className="text-sm text-text-secondary">
                Manage API keys for programmatic access to CranePrice data
              </p>
            </div>
          </div>
          <Button
            variant="default"
            onClick={() => setShowNewKeyForm(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Generate New Key
          </Button>
        </div>

        {/* New API Key Form */}
        {showNewKeyForm && (
          <div className="border border-border rounded-lg p-4 mb-6">
            <h4 className="font-medium text-text-primary mb-4">Generate New API Key</h4>
            <div className="space-y-4">
              <Input
                label="Key Name"
                value={newKeyForm?.name}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="e.g., Production API Key"
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Permissions
                </label>
                <div className="space-y-2">
                  {availablePermissions?.map((permission) => (
                    <label key={permission?.key} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newKeyForm?.permissions?.includes(permission?.key)}
                        onChange={(e) => handlePermissionChange(permission?.key, e?.target?.checked)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-text-primary">{permission?.label}</div>
                        <div className="text-xs text-text-secondary">{permission?.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="default"
                  onClick={handleCreateApiKey}
                  iconName="Key"
                  iconPosition="left"
                >
                  Generate Key
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowNewKeyForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* API Keys List */}
        <div className="space-y-4">
          {apiKeys?.map((apiKey) => (
            <div key={apiKey?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <h4 className="font-medium text-text-primary">{apiKey?.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    apiKey?.status === 'active' ?'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                  }`}>
                    {apiKey?.status}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleApiKeyVisibility(apiKey?.id)}
                    iconName={showApiKey?.[apiKey?.id] ? "EyeOff" : "Eye"}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(apiKey?.key)}
                    iconName="Copy"
                  />
                  {apiKey?.status === 'active' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRevokeApiKey(apiKey?.id)}
                      iconName="Trash2"
                    />
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-text-secondary">Key:</span>
                  <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                    {showApiKey?.[apiKey?.id] ? apiKey?.key : '••••••••••••••••'}
                  </code>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-text-secondary">
                  <span>Created: {formatTimestamp(apiKey?.created)}</span>
                  <span>Last used: {formatTimestamp(apiKey?.lastUsed)}</span>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  {apiKey?.permissions?.map((permission) => (
                    <span key={permission} className="px-2 py-1 bg-muted text-xs rounded">
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Webhooks */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Webhook" size={20} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Webhooks</h3>
              <p className="text-sm text-text-secondary">
                Configure webhooks to receive real-time notifications
              </p>
            </div>
          </div>
          <Button
            variant="default"
            onClick={() => setShowNewWebhookForm(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Webhook
          </Button>
        </div>

        {/* New Webhook Form */}
        {showNewWebhookForm && (
          <div className="border border-border rounded-lg p-4 mb-6">
            <h4 className="font-medium text-text-primary mb-4">Add New Webhook</h4>
            <div className="space-y-4">
              <Input
                label="Webhook URL"
                value={newWebhookForm?.url}
                onChange={(e) => setNewWebhookForm(prev => ({ ...prev, url: e?.target?.value }))}
                placeholder="https://api.example.com/webhooks/cranes"
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Events
                </label>
                <div className="space-y-2">
                  {availableEvents?.map((event) => (
                    <label key={event?.key} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newWebhookForm?.events?.includes(event?.key)}
                        onChange={(e) => handleEventChange(event?.key, e?.target?.checked)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-text-primary">{event?.label}</div>
                        <div className="text-xs text-text-secondary">{event?.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="default"
                  onClick={handleCreateWebhook}
                  iconName="Webhook"
                  iconPosition="left"
                >
                  Create Webhook
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowNewWebhookForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Webhooks List */}
        <div className="space-y-4">
          {webhooks?.map((webhook) => (
            <div key={webhook?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                    {webhook?.url}
                  </code>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    webhook?.status === 'active' ?'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                  }`}>
                    {webhook?.status}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestWebhook(webhook?.id)}
                    iconName="Send"
                  >
                    Test
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteWebhook(webhook?.id)}
                    iconName="Trash2"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-4 text-sm text-text-secondary">
                  <span>Created: {formatTimestamp(webhook?.created)}</span>
                  <span>Last delivery: {formatTimestamp(webhook?.lastDelivery)}</span>
                  <span className={`font-medium ${
                    webhook?.deliveryStatus === 'success' ? 'text-success' : 
                    webhook?.deliveryStatus === 'failed' ? 'text-destructive' : 'text-warning'
                  }`}>
                    {webhook?.deliveryStatus}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {webhook?.events?.map((event) => (
                    <span key={event} className="px-2 py-1 bg-muted text-xs rounded">
                      {event}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* API Documentation */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Book" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">API Documentation</h3>
        </div>
        
        <p className="text-sm text-text-secondary mb-4">
          Learn how to integrate CranePrice API into your applications and workflows.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            iconName="ExternalLink"
            iconPosition="right"
          >
            View Documentation
          </Button>
          <Button
            variant="outline"
            iconName="Code"
            iconPosition="left"
          >
            Code Examples
          </Button>
          <Button
            variant="outline"
            iconName="Zap"
            iconPosition="left"
          >
            Postman Collection
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApiAccessSection;